import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { EventTypes } from '../../../constants/event';
import { selectMenuVersion } from '../../../reducers/menuSlice';
import {
  IMessageReceived,
  messagingActions,
} from '../../../reducers/messagingSlice';
import { selectStage } from '../../../reducers/restaurantSlice';
import logger from '../../../utils/logger';
import { SLICE_NAME } from './aiSuggestions.constants';
import {
  IAISuggestionsState,
  IAISuggestionTransmissionMessage,
  SuggestionVerdict,
} from './aiSuggestions.props';

const initialState: IAISuggestionsState = {
  byType: {},
};

export const aiSuggestionsSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    dismissSuggestion: (
      state,
      {
        payload,
      }: PayloadAction<{ suggestionType: string; suggestionId?: string }>
    ) => {
      delete state.byType[payload.suggestionType];
    },
    // Record the request_id of the agent_cart we just emitted so we can
    // correlate (and discard stale) AI suggestions.
    setLastAgentCartRequestId: (state, { payload }: PayloadAction<string>) => {
      state.lastRequestId = payload;
    },
    clearSuggestions: () => initialState,
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere.
  extraReducers: (builder) => {
    builder.addCase(
      messagingActions.messageReceived,
      (state, action: PayloadAction<IMessageReceived>) => {
        const { messages, taskRouter, sessionBoundarySessionId } =
          action.payload;
        messages.forEach((message) => {
          if (message.event !== EventTypes.aiSuggestion) {
            return;
          }
          if (
            (taskRouter?.currentTaskId &&
              taskRouter?.taskAssignedSessionId !== message.session_id) ||
            (sessionBoundarySessionId &&
              sessionBoundarySessionId !== message.session_id)
          ) {
            // Message belongs to a different session — ignore.
            return;
          }
          const data = (message as IAISuggestionTransmissionMessage).data;
          if (!data?.suggestion_type) {
            return;
          }
          // Retraction: a skip verdict clears any active suggestion of this type.
          if (data.verdict === SuggestionVerdict.skip) {
            delete state.byType[data.suggestion_type];
            return;
          }
          // Stale: this suggestion answers a superseded agent_cart (the agent has
          // since sent a newer one). Proactive suggestions carry no request_id
          // and always pass.
          if (
            data.request_id &&
            state.lastRequestId &&
            data.request_id !== state.lastRequestId
          ) {
            return;
          }
          // A newer suggestion reflects the latest cart, so any previously shown
          // suggestion (of any type) is now stale — replace the map, never
          // accumulate. Only the latest suggestion is ever displayed.
          state.byType = { [data.suggestion_type]: data };
          logger.log({
            message: `AI suggestion received: ${data.suggestion_type} (${data.suggestion_id})`,
          });
        });
      }
    );
    // A suggestion always reflects the current cart, so it must never outlive
    // the order it was made for. Reset on every order/session boundary:
    //  - carEnter: a new car/session arrived (new order context);
    //  - sendEndSession: the current order ended — Cancel and Finish both send
    //    an end-session message (as does the staff-handoff path);
    //  - selectStage / selectMenuVersion: stage/menu re-init (mirrors ai.slice).
    // Without carEnter + sendEndSession a finished/cancelled order's suggestion
    // would linger on screen until the next suggestion happened to replace it.
    builder.addMatcher(
      isAnyOf(
        selectStage.fulfilled,
        selectMenuVersion.fulfilled,
        messagingActions.carEnter,
        messagingActions.sendEndSession
      ),
      () => initialState
    );
  },
});

export const aiSuggestionsActions = aiSuggestionsSlice.actions;
export const {
  dismissSuggestion,
  setLastAgentCartRequestId,
  clearSuggestions,
} = aiSuggestionsSlice.actions;
export default aiSuggestionsSlice.reducer;
